import { useCallback, useEffect, useState } from "react";
import "../working-hours.css";
import { IWorkingHour } from "../../models/IWorkingHour";
import { getWorkingHours } from "../../services/restaurant";
import { IRestaurantAvailability, IScheduleOverride } from "../models/IAvailability";
import { getAvailability, getOverrides } from "../services/availabilityService";
import { apiErrorMessage } from "../utils/workingHoursUtils";
import AvailabilityStatusCard from "./AvailabilityStatusCard";
import WeeklySchedule from "./WeeklySchedule";
import OverridesPanel from "./OverridesPanel";

type Tab = "weekly" | "overrides";

/** Bir restoranın çalışma saatleri: anlık durum, haftalık program ve özel günler. */
export default function WorkingHoursManager({ restaurantId }: { restaurantId: string }) {
  const [tab, setTab] = useState<Tab>("weekly");

  const [hours, setHours] = useState<IWorkingHour[]>([]);
  const [hoursLoading, setHoursLoading] = useState(true);

  const [availability, setAvailability] = useState<IRestaurantAvailability | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");

  const [overrides, setOverrides] = useState<IScheduleOverride[]>([]);
  const [overridesLoading, setOverridesLoading] = useState(true);
  const [overridesError, setOverridesError] = useState("");

  const loadHours = useCallback(() => {
    setHoursLoading(true);
    getWorkingHours(restaurantId)
      .then((res) => setHours(res.data ?? []))
      .catch(() => setHours([]))
      .finally(() => setHoursLoading(false));
  }, [restaurantId]);

  const loadAvailability = useCallback(() => {
    setAvailabilityLoading(true);
    getAvailability(restaurantId)
      .then((res) => { setAvailability(res.data); setAvailabilityError(""); })
      .catch((err) => setAvailabilityError(apiErrorMessage(err, "Durum alınamadı.")))
      .finally(() => setAvailabilityLoading(false));
  }, [restaurantId]);

  const loadOverrides = useCallback(() => {
    setOverridesLoading(true);
    getOverrides(restaurantId)
      .then((res) => { setOverrides(res.data ?? []); setOverridesError(""); })
      .catch((err) => setOverridesError(apiErrorMessage(err, "Özel günler alınamadı.")))
      .finally(() => setOverridesLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    loadHours();
    loadAvailability();
    loadOverrides();
  }, [loadHours, loadAvailability, loadOverrides]);

  // Durum zamanla değişir (süreli kapanış biter, mesai başlar) — dakikada bir tazele
  useEffect(() => {
    const t = setInterval(loadAvailability, 60000);
    return () => clearInterval(t);
  }, [loadAvailability]);

  const afterScheduleChange = () => { loadHours(); loadAvailability(); };
  const afterOverrideChange = () => { loadOverrides(); loadAvailability(); };

  const activeCount = overrides.filter((o) => o.isActive).length;

  return (
    <div>
      <AvailabilityStatusCard
        restaurantId={restaurantId}
        availability={availability}
        hours={hours}
        loading={availabilityLoading}
        error={availabilityError}
        onRefresh={loadAvailability}
        onChanged={afterOverrideChange}
      />

      <div className="wh-tabs">
        <button type="button" className={`wh-tab${tab === "weekly" ? " wh-tab--active" : ""}`} onClick={() => setTab("weekly")}>
          <i className="bi bi-calendar-week me-1"></i>Haftalık Program
        </button>
        <button type="button" className={`wh-tab${tab === "overrides" ? " wh-tab--active" : ""}`} onClick={() => setTab("overrides")}>
          <i className="bi bi-calendar2-event me-1"></i>Özel Günler & Kapanışlar
          {overrides.length > 0 && (
            <span style={{ marginLeft: 6, background: activeCount ? "#dc2626" : "#f59e0b", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: "0.72rem" }}>
              {overrides.length}
            </span>
          )}
        </button>
      </div>

      {tab === "weekly" ? (
        <WeeklySchedule restaurantId={restaurantId} hours={hours} overrides={overrides} loading={hoursLoading} onChanged={afterScheduleChange} />
      ) : (
        <OverridesPanel restaurantId={restaurantId} overrides={overrides} loading={overridesLoading} error={overridesError} onChanged={afterOverrideChange} />
      )}
    </div>
  );
}
